import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Users, Star, Plane, Clock, FileText, Upload, X, CheckCircle2, XCircle,
  Trash2, Edit, Download, BadgeCheck, Building2, Video, Store,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * HR Management (admin-only)
 *
 * Tabs: Overview · Employees · Performance · Leave · Attendance · SOPs
 *
 * Wraps the existing auth users with HR context (employee ID, hire date,
 * KTP/NPWP/BPJS, emergency contact, leave balance, monthly performance
 * reviews, attendance log, SOP acknowledgements). Payroll lives in its
 * own page but salary_base entered here feeds the payslip generator.
 */

const TABS = [
  { key: "overview",   label: "Overview",    icon: BadgeCheck },
  { key: "employees",  label: "Employees",   icon: Users },
  { key: "reviews",    label: "Performance", icon: Star },
  { key: "leave",      label: "Leave",       icon: Plane },
  { key: "attendance", label: "Attendance",  icon: Clock },
  { key: "sops",       label: "SOPs",        icon: FileText },
];

const CONTRACT_LABEL = {
  permanent: "Permanent",
  probation: "Probation",
  contract:  "Contract",
  intern:    "Intern",
};

const WH_ICON = {
  ONLINE_SALE: Building2,
  SHOWROOM: Store,
  LIVE_PRODUCT: Video,
};

const fmtIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function HR() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  if (user?.role !== "admin") {
    return <div className="p-8 text-center text-[#8A8A8A]">HR is admin-only.</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto" data-testid="hr-page">
      <div className="mb-6">
        <div className="overline text-[#D4AF37] flex items-center gap-2">
          <BadgeCheck className="w-3 h-3" /> Opik Jewels · People
        </div>
        <h1 className="serif text-4xl sm:text-5xl lg:text-6xl mt-2">HR Management</h1>
        <p className="text-xs text-[#8A8A8A] mt-2">
          Employees · Performance · Leave · Attendance · SOPs — everything you need for a compliant Indonesian team.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-white/10 mb-8 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`hr-tab-${t.key}`}
              className={`px-4 py-3 text-xs mono uppercase tracking-wider inline-flex items-center gap-2 whitespace-nowrap border-b-2 transition-colors ${
                active ? "text-[#D4AF37] border-[#D4AF37]" : "text-[#8A8A8A] border-transparent hover:text-[#EAEAEA]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview"   && <Overview />}
      {tab === "employees"  && <Employees />}
      {tab === "reviews"    && <Reviews />}
      {tab === "leave"      && <Leave />}
      {tab === "attendance" && <Attendance />}
      {tab === "sops"       && <SOPs />}
    </div>
  );
}

// ---------- OVERVIEW ----------
function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/hr/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#8A8A8A] text-sm">Loading…</div>;
  const cards = [
    { label: "Active headcount",         value: d.headcount_active,       icon: Users,        color: "text-[#D4AF37]" },
    { label: "On leave now",             value: d.on_leave,               icon: Plane,        color: "text-[#7BA585]" },
    { label: "Pending leave requests",   value: d.pending_leave_requests, icon: Clock,        color: "text-[#C99356]" },
    { label: `Reviews this ${d.current_month.slice(-2)}/${d.current_month.slice(0,4).slice(-2)}`, value: d.reviews_this_month, icon: Star, color: "text-[#D4AF37]" },
    { label: "SOPs uploaded",            value: d.sop_count,              icon: FileText,     color: "text-[#EAEAEA]" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className="glass-card p-6" data-testid={`hr-kpi-${c.label.replace(/\s+/g,"-").toLowerCase()}`}>
            <div className={`overline flex items-center gap-2 ${c.color}`}><Icon className="w-3 h-3" /> {c.label}</div>
            <div className="serif text-3xl mt-3">{c.value}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- EMPLOYEES ----------
function Employees() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/hr/employees").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);
  const filtered = rows.filter((r) =>
    (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.employee_id || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.position || "").toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div>
      <input
        placeholder="Search by name, employee ID, position…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="luxe-input w-full sm:w-96 px-4 py-2.5 text-sm mb-6"
        data-testid="hr-emp-search"
      />
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10">
            <tr className="text-[0.6rem] mono uppercase tracking-wider text-[#8A8A8A]">
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Position</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Warehouse</th>
              <th className="text-right px-4 py-3 hidden md:table-cell">Salary Base</th>
              <th className="text-center px-4 py-3 hidden lg:table-cell">Leave</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const WhIcon = WH_ICON[r.default_warehouse] || Building2;
              const lb = r.leave_balance || {};
              return (
                <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`emp-row-${r.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="mono text-[0.7rem] text-[#D4AF37]">{r.employee_id}</div>
                    <div className="text-[#EAEAEA] text-sm">{r.name}</div>
                    <div className="text-[0.65rem] text-[#8A8A8A]">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-[#EAEAEA]">
                    {r.position || "—"}
                    <div className="text-[0.6rem] mono text-[#8A8A8A] uppercase mt-1">{CONTRACT_LABEL[r.contract_type] || r.contract_type}</div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#8A8A8A]">
                    {r.role === "sales" && r.default_warehouse ? (
                      <span className="inline-flex items-center gap-1.5"><WhIcon className="w-3 h-3 text-[#D4AF37]" />{r.default_warehouse.replace("_", " ")}</span>
                    ) : (
                      <span className="text-[#666]">All</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-right text-xs mono text-[#D4AF37]">
                    {r.salary_base > 0 ? fmtIDR(r.salary_base) : "—"}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-center text-[0.65rem] mono text-[#8A8A8A]">
                    <span title="Annual / Sick / Permission">{lb.annual ?? 0} · {lb.sick ?? 0} · {lb.permission ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 text-[0.55rem] mono uppercase tracking-wider border ${
                      r.status === "active" ? "text-[#7BA585] border-[#7BA585]/40" :
                      r.status === "on_leave" ? "text-[#C99356] border-[#C99356]/40" :
                      "text-red-400 border-red-400/40"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(r)} className="btn-outline-gold px-3 py-1.5 text-[0.65rem] inline-flex items-center gap-1.5" data-testid={`emp-edit-${r.employee_id}`}>
                      <Edit className="w-3 h-3" /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[#8A8A8A] text-sm">No employees.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && <EmployeeEditModal emp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EmployeeEditModal({ emp, onClose, onSaved }) {
  const [form, setForm] = useState({
    hire_date: emp.hire_date || "",
    position: emp.position || "",
    contract_type: emp.contract_type || "permanent",
    salary_base: emp.salary_base || 0,
    ktp_number: emp.ktp_number || "",
    npwp_number: emp.npwp_number || "",
    bpjs_number: emp.bpjs_number || "",
    emergency_contact: emp.emergency_contact || { name: "", phone: "", relationship: "" },
    leave_balance: emp.leave_balance || { annual: 12, sick: 12, permission: 3 },
    status: emp.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/hr/employees/${emp.user_id}`, { ...form, user_id: emp.user_id });
      toast.success("Employee updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 sm:p-8 overflow-y-auto z-50" onClick={onClose}>
      <div className="glass-card w-full max-w-3xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="hr-emp-modal">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline text-[#D4AF37]">{emp.employee_id}</div>
            <h2 className="serif text-2xl mt-1">{emp.name}</h2>
            <div className="text-xs text-[#8A8A8A] mt-1">{emp.email} · {emp.role}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A8A] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-6">
          <Section title="Employment">
            <Grid>
              <Field label="Hire Date" type="date" v={form.hire_date} on={(v) => setForm({ ...form, hire_date: v })} testid="edit-hire-date" />
              <Field label="Position" v={form.position} on={(v) => setForm({ ...form, position: v })} testid="edit-position" />
              <SelectField label="Contract Type" v={form.contract_type} on={(v) => setForm({ ...form, contract_type: v })}
                options={[["permanent","Permanent"],["probation","Probation"],["contract","Contract"],["intern","Intern"]]} />
              <Field label="Monthly Salary Base (IDR)" type="number" v={form.salary_base} on={(v) => setForm({ ...form, salary_base: Number(v) })} testid="edit-salary" />
              <SelectField label="Status" v={form.status} on={(v) => setForm({ ...form, status: v })}
                options={[["active","Active"],["on_leave","On Leave"],["terminated","Terminated"]]} />
            </Grid>
          </Section>

          <Section title="Documents (Indonesian)">
            <Grid>
              <Field label="KTP Number (NIK)" v={form.ktp_number} on={(v) => setForm({ ...form, ktp_number: v })} testid="edit-ktp" />
              <Field label="NPWP Number" v={form.npwp_number} on={(v) => setForm({ ...form, npwp_number: v })} />
              <Field label="BPJS Number" v={form.bpjs_number} on={(v) => setForm({ ...form, bpjs_number: v })} />
            </Grid>
          </Section>

          <Section title="Emergency Contact">
            <Grid>
              <Field label="Name" v={form.emergency_contact.name} on={(v) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, name: v } })} />
              <Field label="Phone" v={form.emergency_contact.phone} on={(v) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, phone: v } })} />
              <Field label="Relationship" v={form.emergency_contact.relationship} on={(v) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, relationship: v } })} />
            </Grid>
          </Section>

          <Section title="Leave Balance (days remaining)">
            <Grid cols={3}>
              <Field label="Annual" type="number" v={form.leave_balance.annual} on={(v) => setForm({ ...form, leave_balance: { ...form.leave_balance, annual: Number(v) } })} />
              <Field label="Sick" type="number" v={form.leave_balance.sick} on={(v) => setForm({ ...form, leave_balance: { ...form.leave_balance, sick: Number(v) } })} />
              <Field label="Permission" type="number" v={form.leave_balance.permission} on={(v) => setForm({ ...form, leave_balance: { ...form.leave_balance, permission: Number(v) } })} />
            </Grid>
          </Section>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-white/10">
          <button onClick={onClose} className="btn-outline-gold px-6 py-2 text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-gold px-6 py-2 text-xs disabled:opacity-50" data-testid="hr-emp-save">
            {busy ? "Saving…" : "Save Employee"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- REVIEWS ----------
function Reviews() {
  const [emps, setEmps] = useState([]);
  const [selUser, setSelUser] = useState("");
  const [reviews, setReviews] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get("/hr/employees").then((r) => setEmps(r.data)); }, []);
  useEffect(() => {
    if (!selUser) return;
    api.get(`/hr/employees/${selUser}/reviews`).then((r) => setReviews(r.data));
  }, [selUser]);

  const selectedEmp = emps.find((e) => e.user_id === selUser);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select value={selUser} onChange={(e) => setSelUser(e.target.value)} className="luxe-input px-4 py-2.5 text-sm flex-1" data-testid="review-emp-select">
          <option value="">Select employee…</option>
          {emps.map((e) => <option key={e.user_id} value={e.user_id}>{e.employee_id} · {e.name}</option>)}
        </select>
        {selUser && (
          <button onClick={() => setCreating(true)} className="btn-gold px-5 py-2.5 text-xs inline-flex items-center gap-2" data-testid="review-new">
            <Star className="w-3.5 h-3.5" /> New Review
          </button>
        )}
      </div>

      {selectedEmp && (
        <div className="glass-card divide-y divide-white/5">
          {reviews.length === 0 && <div className="p-8 text-center text-[#8A8A8A] text-sm">No reviews yet for this employee.</div>}
          {reviews.map((r) => (
            <div key={r.id} className="p-5" data-testid={`review-${r.month}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="overline text-[#D4AF37]">{r.month}</div>
                <div className="flex text-[#D4AF37]">{"★".repeat(r.rating)}<span className="text-[#333]">{"★".repeat(5 - r.rating)}</span></div>
              </div>
              {r.notes && <div className="text-sm text-[#EAEAEA] mt-2 whitespace-pre-wrap">{r.notes}</div>}
              {r.role_kpis && Object.keys(r.role_kpis).length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(r.role_kpis).map(([k, v]) => (
                    <div key={k} className="border border-white/5 p-2">
                      <div className="text-[0.55rem] mono uppercase text-[#8A8A8A]">{k.replace(/_/g, " ")}</div>
                      <div className="text-sm text-[#EAEAEA]">{typeof v === "number" ? fmtIDR(v) : v}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[0.6rem] mono text-[#666] mt-3">by {r.created_by} · {(r.created_at || "").slice(0, 10)}</div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ReviewCreateModal
          employee={selectedEmp}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); api.get(`/hr/employees/${selUser}/reviews`).then((r) => setReviews(r.data)); }}
        />
      )}
    </div>
  );
}

function ReviewCreateModal({ employee, onClose, onSaved }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(currentMonth);
  const [rating, setRating] = useState(4);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/employees/${employee.user_id}/reviews`, { month, rating, notes });
      toast.success("Review saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 sm:p-8 z-50 overflow-y-auto" onClick={onClose}>
      <div className="glass-card w-full max-w-lg p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="review-create-modal">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline text-[#D4AF37]">Monthly Review</div>
            <h2 className="serif text-2xl mt-1">{employee.name}</h2>
          </div>
          <button onClick={onClose} className="text-[#8A8A8A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Month" type="month" v={month} on={setMonth} testid="review-month" />
          <div>
            <label className="text-[0.65rem] mono uppercase tracking-wider text-[#8A8A8A]">Rating (1–5)</label>
            <div className="flex gap-2 mt-2" data-testid="review-stars">
              {[1,2,3,4,5].map((n) => (
                <button key={n} onClick={() => setRating(n)} className={`text-3xl ${n <= rating ? "text-[#D4AF37]" : "text-[#333]"} hover:text-[#D4AF37]`}>★</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[0.65rem] mono uppercase tracking-wider text-[#8A8A8A]">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} className="luxe-input w-full px-3 py-2 text-sm mt-2" placeholder="Strengths, areas for improvement, action items…" data-testid="review-notes" />
          </div>
          {employee.role === "sales" && (
            <div className="text-[0.65rem] mono text-[#C99356] p-3 border border-[#C99356]/30 bg-[#C99356]/[0.05]">
              Sales revenue for this month will be auto-attached to the review.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn-outline-gold px-5 py-2 text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-gold px-5 py-2 text-xs disabled:opacity-50" data-testid="review-save">
            {busy ? "Saving…" : "Save Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- LEAVE ----------
function Leave() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/hr/leave-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/hr/employees").then((r) => setEmps(r.data)); }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/hr/leave-requests/${id}`, { status });
      toast.success(`Leave ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setCreating(true)} className="btn-gold px-5 py-2 text-xs inline-flex items-center gap-2" data-testid="leave-new">
          <Plane className="w-3.5 h-3.5" /> Log Leave
        </button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-[0.6rem] mono uppercase tracking-wider text-[#8A8A8A]">
            <tr>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Dates</th>
              <th className="text-center px-4 py-3">Days</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Reason</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5" data-testid={`leave-${r.id}`}>
                <td className="px-4 py-3 text-[#EAEAEA]">{r.employee_name}</td>
                <td className="px-4 py-3 text-xs text-[#8A8A8A] uppercase">{r.leave_type}</td>
                <td className="px-4 py-3 text-xs text-[#EAEAEA]">{r.start_date} → {r.end_date}</td>
                <td className="px-4 py-3 text-center mono text-[#D4AF37]">{r.days}</td>
                <td className="px-4 py-3 text-xs text-[#8A8A8A] hidden md:table-cell max-w-xs truncate">{r.reason}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 text-[0.55rem] mono uppercase border ${
                    r.status === "approved" ? "text-[#7BA585] border-[#7BA585]/40" :
                    r.status === "rejected" ? "text-red-400 border-red-400/40" :
                    "text-[#C99356] border-[#C99356]/40"
                  }`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending" && (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => decide(r.id, "approved")} className="text-[#7BA585] hover:text-[#7BA585]/70" title="Approve" data-testid={`leave-approve-${r.id}`}><CheckCircle2 className="w-4 h-4" /></button>
                      <button onClick={() => decide(r.id, "rejected")} className="text-red-400 hover:text-red-400/70" title="Reject" data-testid={`leave-reject-${r.id}`}><XCircle className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8A8A8A] text-sm">No leave requests.</td></tr>}
          </tbody>
        </table>
      </div>
      {creating && <LeaveCreateModal emps={emps} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function LeaveCreateModal({ emps, onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [leaveType, setLeaveType] = useState("annual");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!userId || !start || !end) return toast.error("Employee, start, and end dates required");
    setBusy(true);
    try {
      await api.post(`/hr/employees/${userId}/leave`, { leave_type: leaveType, start_date: start, end_date: end, reason });
      toast.success("Leave logged");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="glass-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="leave-create-modal">
        <div className="flex justify-between items-start mb-4"><h3 className="serif text-xl">New Leave Request</h3><button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button></div>
        <div className="space-y-3">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm" data-testid="leave-emp">
            <option value="">Employee…</option>
            {emps.map((e) => <option key={e.user_id} value={e.user_id}>{e.employee_id} · {e.name}</option>)}
          </select>
          <SelectField label="Type" v={leaveType} on={setLeaveType} options={[["annual","Annual"],["sick","Sick"],["permission","Permission (izin)"]]} />
          <Grid cols={2}>
            <Field label="Start date" type="date" v={start} on={setStart} testid="leave-start" />
            <Field label="End date" type="date" v={end} on={setEnd} testid="leave-end" />
          </Grid>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="luxe-input w-full px-3 py-2 text-sm" placeholder="Reason…" data-testid="leave-reason" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-outline-gold px-4 py-2 text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="leave-save">{busy ? "…" : "Log"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- ATTENDANCE ----------
function Attendance() {
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState([]);
  const [selUser, setSelUser] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [entering, setEntering] = useState(false);
  const load = () => {
    const params = new URLSearchParams();
    if (selUser) params.set("user_id", selUser);
    if (month) params.set("month", month);
    api.get(`/hr/attendance?${params.toString()}`).then((r) => setRows(r.data));
  };
  useEffect(() => { api.get("/hr/employees").then((r) => setEmps(r.data)); }, []);
  useEffect(() => { load(); }, [selUser, month]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6 items-start sm:items-center">
        <select value={selUser} onChange={(e) => setSelUser(e.target.value)} className="luxe-input px-4 py-2.5 text-sm" data-testid="att-emp">
          <option value="">All employees</option>
          {emps.map((e) => <option key={e.user_id} value={e.user_id}>{e.employee_id} · {e.name}</option>)}
        </select>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="luxe-input px-4 py-2.5 text-sm" data-testid="att-month" />
        <button onClick={() => setEntering(true)} className="btn-gold px-5 py-2 text-xs inline-flex items-center gap-2 ml-auto" data-testid="att-new">
          <Clock className="w-3.5 h-3.5" /> Record
        </button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-[0.6rem] mono uppercase tracking-wider text-[#8A8A8A]">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-center px-4 py-3">In</th>
              <th className="text-center px-4 py-3">Out</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const emp = emps.find((e) => e.user_id === r.user_id);
              return (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="px-4 py-3 mono text-[#EAEAEA]">{r.date}</td>
                  <td className="px-4 py-3 text-xs text-[#EAEAEA]">{emp?.name || r.user_id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-center mono text-[#7BA585]">{r.check_in || "—"}</td>
                  <td className="px-4 py-3 text-center mono text-[#C99356]">{r.check_out || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#8A8A8A] hidden md:table-cell max-w-xs truncate">{r.note}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8A8A8A] text-sm">No attendance records.</td></tr>}
          </tbody>
        </table>
      </div>
      {entering && <AttEntryModal emps={emps} onClose={() => setEntering(false)} onSaved={() => { setEntering(false); load(); }} />}
    </div>
  );
}

function AttEntryModal({ emps, onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!userId || !date) return toast.error("Employee & date required");
    setBusy(true);
    try {
      await api.post(`/hr/employees/${userId}/attendance`, { date, check_in: checkIn, check_out: checkOut, note });
      toast.success("Attendance recorded");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="glass-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="att-modal">
        <div className="flex justify-between items-start mb-4"><h3 className="serif text-xl">Record Attendance</h3><button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button></div>
        <div className="space-y-3">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm" data-testid="att-modal-emp">
            <option value="">Employee…</option>
            {emps.map((e) => <option key={e.user_id} value={e.user_id}>{e.employee_id} · {e.name}</option>)}
          </select>
          <Field label="Date" type="date" v={date} on={setDate} />
          <Grid cols={2}>
            <Field label="Check-in" type="time" v={checkIn} on={setCheckIn} />
            <Field label="Check-out" type="time" v={checkOut} on={setCheckOut} />
          </Grid>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="luxe-input w-full px-3 py-2 text-sm" placeholder="Note (optional)…" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-outline-gold px-4 py-2 text-xs">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="att-save">{busy ? "…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- SOPs ----------
function SOPs() {
  const [sops, setSops] = useState([]);
  const [emps, setEmps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [ackSop, setAckSop] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.get("/hr/sops").then((r) => setSops(r.data));
  useEffect(() => { load(); api.get("/hr/employees").then((r) => setEmps(r.data)); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("PDF only");
    if (file.size > 10 * 1024 * 1024) return toast.error("Max 10 MB");
    const title = prompt("SOP title:", file.name.replace(/\.pdf$/i, ""));
    if (!title) return;
    const description = prompt("Description (optional):", "") || "";
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const q = `?title=${encodeURIComponent(title)}&description=${encodeURIComponent(description)}`;
      await api.post(`/hr/sops${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SOP uploaded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this SOP? Acknowledgements will be cleared.")) return;
    try { await api.delete(`/hr/sops/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <input ref={fileRef} type="file" accept="application/pdf" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="sop-file" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-gold px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="sop-upload">
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload SOP (PDF)"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sops.map((s) => (
          <div key={s.id} className="glass-card p-5" data-testid={`sop-${s.id}`}>
            <div className="flex items-start gap-3">
              <FileText className="w-6 h-6 text-[#D4AF37] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="serif text-lg text-[#EAEAEA]">{s.title}</div>
                {s.description && <div className="text-xs text-[#8A8A8A] mt-1">{s.description}</div>}
                <div className="text-[0.6rem] mono text-[#666] mt-2">
                  {(s.uploaded_at || "").slice(0, 10)} · by {s.uploaded_by} · {s.ack_count} acknowledgement{s.ack_count === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <a href={s.file_url} target="_blank" rel="noreferrer" className="btn-outline-gold px-3 py-1.5 text-[0.65rem] inline-flex items-center gap-1.5"><Download className="w-3 h-3" /> View PDF</a>
              <button onClick={() => setAckSop(s)} className="btn-outline-gold px-3 py-1.5 text-[0.65rem] inline-flex items-center gap-1.5" data-testid={`sop-ack-btn-${s.id}`}><CheckCircle2 className="w-3 h-3" /> Acknowledge</button>
              <button onClick={() => del(s.id)} className="text-red-400 hover:text-red-500 ml-auto" title="Delete SOP" data-testid={`sop-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {sops.length === 0 && <div className="col-span-2 text-center py-14 text-[#8A8A8A] text-sm">No SOPs uploaded yet. Drop your first PDF (dress code, cash handling, live-stream script…) to get started.</div>}
      </div>
      {ackSop && <SopAckModal sop={ackSop} emps={emps} onClose={() => setAckSop(null)} onSaved={() => { setAckSop(null); load(); }} />}
    </div>
  );
}

function SopAckModal({ sop, emps, onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [acks, setAcks] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/hr/sops/${sop.id}/acknowledgements`).then((r) => setAcks(r.data)); }, [sop.id]);
  const record = async () => {
    if (!userId) return toast.error("Select an employee");
    setBusy(true);
    try {
      await api.post(`/hr/sops/${sop.id}/acknowledge`, { user_id: userId });
      toast.success("Acknowledged");
      const r = await api.get(`/hr/sops/${sop.id}/acknowledgements`);
      setAcks(r.data);
      setUserId("");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="glass-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="sop-ack-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="overline text-[#D4AF37]">Acknowledgement</div>
            <h3 className="serif text-xl mt-1">{sop.title}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="luxe-input flex-1 px-3 py-2 text-sm">
            <option value="">Employee…</option>
            {emps.map((e) => <option key={e.user_id} value={e.user_id}>{e.employee_id} · {e.name}</option>)}
          </select>
          <button onClick={record} disabled={busy} className="btn-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="sop-ack-save">Record</button>
        </div>
        <div className="text-[0.65rem] mono uppercase tracking-wider text-[#8A8A8A] mb-2">Already acknowledged ({acks.length})</div>
        <div className="divide-y divide-white/5 max-h-64 overflow-y-auto">
          {acks.map((a) => (
            <div key={a.user_id} className="py-2 flex justify-between items-center text-xs">
              <div className="text-[#EAEAEA]">{a.employee_name}</div>
              <div className="mono text-[#666]">{(a.acknowledged_at || "").slice(0, 10)}</div>
            </div>
          ))}
          {acks.length === 0 && <div className="text-center py-4 text-[#666] text-xs">No acknowledgements yet.</div>}
        </div>
      </div>
    </div>
  );
}

// ---------- shared UI helpers ----------
function Section({ title, children }) {
  return (
    <div>
      <div className="overline text-[#D4AF37] mb-3">{title}</div>
      {children}
    </div>
  );
}
function Grid({ cols = 2, children }) {
  return <div className={`grid grid-cols-1 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-4`}>{children}</div>;
}
function Field({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-[0.6rem] mono uppercase tracking-wider text-[#8A8A8A]">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm mt-1.5" data-testid={testid} />
    </div>
  );
}
function SelectField({ label, v, on, options }) {
  return (
    <div>
      <label className="text-[0.6rem] mono uppercase tracking-wider text-[#8A8A8A]">{label}</label>
      <select value={v} onChange={(e) => on(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm mt-1.5">
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}
